import React, { Fragment } from 'react'
import moment from 'moment'
import { useMediaQuery } from 'react-responsive'
import { StyledChart, StyledChartAndChildren, TooltipTemplate } from './styled'
import {
	AreaChart,
	Area,
	XAxis,
	YAxis,
	Tooltip,
	ResponsiveContainer,
	CartesianGrid,
	BarChart,
	Bar
	//Brush
} from 'recharts'
import { dateFormat } from '../DatePicker/Picker'
import LDChart from './charts/LineDashboardChart'
import BDChart from './charts/BarDashboardChart'
import { useTranslation } from 'react-i18next'
import { sizes } from 'helpers/utils'
import { useWindowSize } from 'hooks/useWindowSize'
import PreparedMessage from 'components/PreparedMessage/PreparedMessage'

const renderLineTooltipTemplate = (choosenLabel, t = () => { }) => ({ active, payload }) => {
	if (active) {
		const todayData = payload[0] || {}
		const compareData = payload[1] || null
		const { dateTime } = todayData.payload || {}
		const currentDate = dateTime && moment(dateTime)
		const headerValue = `${currentDate.clone().subtract(15, 'minutes').format('HH:mm')} - ${currentDate.format('HH:mm')}`
		const customersToday = todayData?.payload[todayData.name]
		const customersCompare = compareData?.payload[compareData.name]
		return (customersToday || customersCompare) ? (
			<TooltipTemplate>
				<div className='tooltip__header'>
					<h4>{t('loyalty_card_section.strings.period')}:</h4>
					<span>{headerValue}</span>
				</div>
				<div className='tooltip__footer_today'>
					<h4>{t('dashboard_section.customers')} ({t('dashboard_section.today')}):</h4>
					<span>{customersToday}</span>
				</div>
				{typeof customersCompare !== 'undefined' ? <div className='tooltip__footer_comparison'>
					<h4>{t('dashboard_section.customers')} ({choosenLabel}):</h4>
					<span>{customersCompare}</span>
				</div> : null}
			</TooltipTemplate>
		) : null
	}
	return null
}

export const renderBarTooltipTemplate = (t = () => { }, { currency, label = 'dashboard_section.customers' } = {}) => ({ active, payload }) => {
	if (active) {
		const { date, customers, total } = payload[0] ? payload[0].payload : {}
		return date && (total || customers) ? (
			<TooltipTemplate>
				<div className='tooltip__header'>
					<h4>{t('common.fields.date')}:</h4>
					<span>{moment(date, 'YYYY-MM-DD').format(dateFormat)}</span>
				</div>
				<div className='tooltip__footer_today'>
					<h4>{t(label)}:</h4>
					<span>{currency ? `${total.toFixed(2)} ${currency}` : customers || 0}</span>
				</div>
			</TooltipTemplate>
		) : null
	}
	return null
}

const BarDashboardChart = ({ data, isMobile }) => {
	const [t] = useTranslation()
	return (
		<ResponsiveContainer height={isMobile ? 200 : 400}>
			<BarChart
				data={data}
				margin={{ left: -30 }}
			>
				<defs>
					<linearGradient id='bar-gradient' x1='0' y1='0' x2='0' y2='1'>
						<stop offset='56%' stopColor='#53cab4' stopOpacity={0.2} />
						<stop offset='100%' stopColor='#53cab4' stopOpacity={0} />
					</linearGradient>
				</defs>
				<CartesianGrid strokeDasharray='3 3' vertical={false} />
				<XAxis dataKey='day' />
				<YAxis />
				<Tooltip content={renderBarTooltipTemplate(t)} />
				<Bar dataKey='customers' fill={`url(#bar-gradient)`} />
			</BarChart>
		</ResponsiveContainer>
	)
}

const LineDashboardChart = ({ data, series, choosenLabel, isMobile }) => {
	const [t] = useTranslation()
	return (
		<ResponsiveContainer height={isMobile ? 200 : 400}>
			<AreaChart data={data} margin={{ left: -10 }}>
				<defs>
					{series.map(({ color }, index) => {
						return (
							<linearGradient className={`grad_field${index}`} key={index} id={`color${index}${color}`} x1='0' y1='0' x2='0' y2='1'>
								<stop offset='56%' stopColor={color} stopOpacity={0.17} />
								<stop offset='100%' stopColor={color} stopOpacity={0} />
							</linearGradient>
						)
					})}
				</defs>
				<CartesianGrid strokeDasharray='3 3' vertical={false} />
				<XAxis
					scale='time'
					type='number'
					dataKey='dateTime'
					interval={4}
					domain={['dataMin', 'dataMax']}
					tickFormatter={(tick) => moment(tick).format('HH:mm')}
				/>
				<YAxis allowDecimals={false} />
				<Tooltip content={renderLineTooltipTemplate(choosenLabel, t)} />
				{series.map(({ color, value }, index) => {
					return (
						<Area
							key={index}
							type='monotone'
							fillOpacity={1}
							dataKey={value}
							stroke={color}
							fill={`url(#color${index}${color})`}
						/>
					)
				})}
			</AreaChart>
		</ResponsiveContainer>
	)
}

const DashboardChart = (props) => {
	const { headerItems = [], type = 'line', dataLoading, fetchError, children, children2 } = props
	let currentChart = null
	const windowSize = useWindowSize()
	const isMobile = useMediaQuery({ query: `(max-width: ${sizes.mobile}px)` })
	const isTablet = useMediaQuery({ query: `(max-width: ${sizes.tablet}px)` })
	props = { ...props, isMobile, isTablet } // eslint-disable-line no-param-reassign
	switch (type) {
		case 'line':
			currentChart = <LineDashboardChart {...props} />
			break
		case 'bar':
			currentChart = <BarDashboardChart {...props} />
			break
		case 'ldchart':
			currentChart = <LDChart {...props} />
			break
		case 'bdchart':
			currentChart = <BDChart {...props} />
			break
		default:
			currentChart = `Unknown type`
	}
	const items = headerItems.map((item, index) => <Fragment key={index}>{item}</Fragment>)
	const containerProps = {
		style: { width: isMobile ? windowSize.width - 20 : '100%' }
	}

	let dataForShow
	if(fetchError) {
		dataForShow = <PreparedMessage type='error' />
	}else if(!dataLoading && !fetchError && (type === 'datagrid' || type === 'ldchart' || type === 'bdchart' ? true : props.data && props.data.length)) {
		dataForShow = currentChart
	}else if(dataLoading) {
		dataForShow = <PreparedMessage type='loading' />
	}else{
		dataForShow = <PreparedMessage type='noData' />
	}

	return (
		<>
			<StyledChart
				className='dchart__mainBlock'
				{...containerProps}
			>
				{items &&
					<div className='dchart__header'>
						{items}
					</div>
				}
				<StyledChartAndChildren
					className='dchart__chartAndChildren'
				>
					{children}
					{dataForShow}
					{children2}
				</StyledChartAndChildren>
			</StyledChart>
		</>
	)
}

export default DashboardChart
